import React, { useState, useRef, useEffect } from "react";
import { ReactComponent as AlarmClock } from '../../images/icons/alarm-clock-regular.svg';

interface QuestionTimerProps {
    timeLimit: number;
    onComplete?: () => void;
    inOverlay?: boolean;
    isActive?: boolean;
}

const Timer: React.FC<QuestionTimerProps> = ({ timeLimit, onComplete, inOverlay = false, isActive = true }) => {
    const [progress, setProgress] = useState(100);
    const [timeLeft, setTimeLeft] = useState(timeLimit);
    const [color, setColor] = useState('#4CAF50');
    const [isShaking, setIsShaking] = useState(false);
    const [opacity, setOpacity] = useState(1);

    const hasCompletedRef = useRef(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const radius = 50;
    const stroke = 4;
    const normalizedRadius = radius - stroke * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    const strokeDashoffset = -1 * (circumference - (progress / 100) * circumference);

    useEffect(() => {
        if (!isActive) {
            clearInterval(intervalRef.current!);
            setOpacity(0);
            return;
        }

        // Reset states
        hasCompletedRef.current = false;
        setIsShaking(false);
        setOpacity(1);
        setProgress(100);
        setTimeLeft(timeLimit);
        setColor('#4CAF50');

        intervalRef.current = setInterval(() => {
            setTimeLeft((prevTimeLeft) => {
                const newTimeLeft = prevTimeLeft - 0.1;
                if (newTimeLeft <= 0) {
                    if (!hasCompletedRef.current) {
                        hasCompletedRef.current = true;
                        clearInterval(intervalRef.current!);
                        setOpacity(0);
                        onComplete?.();
                    }
                    return 0;
                }

                setProgress((newTimeLeft / timeLimit) * 100);

                if (newTimeLeft <= timeLimit * 0.6) setColor('#FFA500');
                if (newTimeLeft <= timeLimit * 0.3) {
                    setColor('#FF0000');
                    setIsShaking(true);
                }

                return newTimeLeft;
            });
        }, 100);

        return () => clearInterval(intervalRef.current!);
    }, [isActive, timeLimit, onComplete]);

    return (
        <div
            className={`timer-container ${inOverlay ? '' : 'no-overlay'}`}
            style={{ opacity, transition: 'opacity 0.5s ease-out' }}
        >
            <svg height={radius * 2} width={radius * 2} className="timer-circle">
                <circle stroke="#ddd" fill="transparent" strokeWidth={stroke} r={normalizedRadius} cx={radius} cy={radius} />
                <circle
                    stroke={color}
                    fill="transparent"
                    strokeWidth={stroke}
                    strokeDasharray={`${circumference} ${circumference}`}
                    style={{ strokeDashoffset }}
                    r={normalizedRadius}
                    cx={radius}
                    cy={radius}
                    transform={`rotate(-90 ${radius} ${radius})`}
                />
            </svg>
            <div className="timer-wrapper">
                <AlarmClock className={`timer-icon ${isShaking ? 'shake' : ''}`} />
            </div>
        </div>
    );
};

export default React.memo(Timer);